/**
 * writeQueue/dispatch.ts — pure functions that execute each mutation op
 * against Supabase.
 *
 * Centralizes every editorial mutation in one file. Each dispatcher takes
 * a Supabase client + payload, returns `Promise<{ error: string | null }>`.
 * The sync worker (sync.ts) routes pending queue items here based on
 * record.op.
 *
 * Plan reference: docs/archive/admin-rebuild/admin-fragility-rebuild-plan.md § Item 1.
 *
 * Status: LIVE (2026-05-19). Every editor-driven mutation in
 * EditorialQueue.tsx now routes through these dispatchers (with inline-
 * Supabase fallback for IndexedDB-unavailable environments).
 *
 * Idempotency notes
 * -----------------
 * - `approve_draft` / `reject_draft` — server-side RPC, idempotent by
 *   `p_draft_id` (re-rejecting inserts a second audit row but lands on
 *   the same end state; that's acceptable for the append-only log).
 * - `update_status_held` / `update_status_rejected` — re-setting the same
 *   status is a no-op. Safe to retry.
 * - `update_content` — overwrites with the latest payload. Safe to retry;
 *   semantics are "last-write-wins" which matches editor intent.
 * - `update_source_rating` — naturally NOT idempotent (INSERT into
 *   source_quality_signals). The dispatcher pre-checks for an existing
 *   editor_rating row for the same (vote_id, source_url, rated_by) tuple
 *   and skips the INSERT if found. TOCTOU race acceptable for the
 *   single-editor admin case; a unique constraint at the DB level is the
 *   long-term fix.
 * - `comment_append` / `agent_reply` — use a client-generated marker
 *   embedded in the comment text. The dispatcher fetches editor_notes
 *   first, checks for the marker, skips if present.
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import { resolveAuthEnv } from '../supabase';

export interface DispatchResult {
  error: string | null;
}

// ============================================================================
// Server-tier helper
// ============================================================================

/**
 * Call a Pages Function endpoint and translate the response into one of:
 *   - { ok: true }                         → server-tier succeeded
 *   - { ok: false, fallback: true, ... }   → fall back to direct Supabase
 *   - { ok: false, fallback: false, ... }  → real validation error, surface it
 *
 * Falls back on: network error, AbortError (timeout), 404 (function not
 * deployed yet), 5xx (server error), and always on dev-stage, which the server
 * tier does not serve (it is never called there). Surfaces directly on: 4xx
 * other than 404 (real client-side validation failure).
 *
 * Client-side timeout 10s. The function itself has a 5s upstream cap on
 * Supabase, so 10s covers full-budget upstream + reasonable network.
 */
/**
 * The admin's database doors (CS-5, 2026-09-28). Schema `public` is closed to signed-in callers
 * since CS-4, so the direct-Supabase fallbacks below go through `api_admin` functions: whole-row
 * read doors named after their table, `<table>_update(p_id, p_fields)` / `<table>_insert(p_fields)`
 * write doors that accept only the fields the admin writes and raise on a missing row, and
 * same-shape wrappers of approve_draft / reject_draft / votd_triage_proposal. Untyped on purpose,
 * as the direct `.from()` calls were.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function doors(client: SupabaseClient): any {
  return client.schema('api_admin');
}

async function callServerTier(
  endpoint: string,
  payload: unknown,
): Promise<
  | { ok: true }
  | { ok: false; fallback: boolean; error: string }
> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 10000);
  try {
    // Findings row 67. The server tier used to receive no target at all, so it wrote to
    // whatever one fixed pair of Cloudflare env vars named — while the operator switched
    // projects by tab. The tab controlled reads and had no bearing on writes, and a write
    // that went to the wrong project looked exactly like one that worked.
    //
    // resolveAuthEnv() is the SAME function getClient() resolves through, so the write path
    // and the read path now answer "which project am I on" identically. The server has no
    // default: an absent or unrecognised header is a 400, not a quiet fallback to Proto.
    const project = resolveAuthEnv(window.location.pathname, window.location.search);

    // dev-stage writes go straight to the database doors (2026-11-03). The Pages Functions know only
    // the projects they hold a service-role key for (functions/api/admin/_project.ts: "prototype",
    // "stage"), so "devstage" was refused with a 400 on every Edit and Approve, and a 400 is not a
    // fallback case: the write never landed. Rather than put dev-stage's master key in the production
    // admin's server environment, the dispatcher takes the path it already takes when the server tier
    // is down: the api_admin door, signed in as the editor. The approve and reject doors take the actor
    // from that session (public.votd_editorial_actor refuses any other id), so nothing is weaker for
    // the writes that carry an actor check. Prod ("stage") still goes through the server tier.
    if (project === 'devstage') {
      return { ok: false, fallback: true, error: 'dev-stage writes use the database doors, not the server tier' };
    }

    const res = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-votd-project': project },
      body: JSON.stringify(payload),
      signal: controller.signal,
    });
    if (res.ok) return { ok: true };
    if (res.status >= 400 && res.status < 500 && res.status !== 404) {
      const body = await res.json().catch(() => ({ error: `HTTP ${res.status}` })) as { error?: string };
      return { ok: false, fallback: false, error: body.error ?? `server-tier rejected with ${res.status}` };
    }
    return { ok: false, fallback: true, error: `server-tier ${endpoint} returned ${res.status}` };
  } catch (err) {
    const e = err as Error;
    const reason = e.name === 'AbortError' ? 'timed out after 10s' : `threw: ${e.message}`;
    return { ok: false, fallback: true, error: `server-tier ${endpoint} ${reason}` };
  } finally {
    clearTimeout(timer);
  }
}

// ============================================================================
// Op-specific payload types
// ============================================================================

export interface ApproveDraftPayload {
  p_draft_id: string;
  p_fast_track: boolean;
  p_quality: string | null;
  p_approved_by: string | null;
  p_intervention_level: string;
  p_fields_edited: string[];
  p_intervention_notes: string | null;
  p_signals: Array<{ source_url: string; source_domain: string }>;
}

export interface UpdateStatusHeldPayload {
  draftId: string;
}

export interface UpdateStatusRejectedPayload {
  draftId: string;
  reject_reason: string;
  reject_notes: string | null;
  rejected_by: string | null;
}

export interface RejectDraftPayload {
  p_draft_id: string;
  p_reason: string;
  p_notes: string | null;
  p_rejected_by: string | null;
}

export interface UpdateContentPayload {
  draftId: string;
  /**
   * Map of column name → value for the sourced_queue UPDATE. Use any
   * combination of columns: e.g. `{ poll_question: 'new', updated_at: '...' }`
   * for a question edit, or `{ editor_notes: 'note', status: 'commenting',
   * commented_at: '...' }` for a comment submission. Whatever fields land
   * in this object are passed straight to .update() as the row patch.
   *
   * Was a single-field type until 2026-05-19; widened to support
   * handleEditField's multi-field writes (the comment-append flow updates
   * editor_notes + status + commented_at in one go).
   */
  fields: Record<string, string | string[] | number | boolean | null>;
}

export interface TriageProposalPayload {
  p_proposal_id: string;
  p_outcome: 'to_draft' | 'parked' | 'duplicate' | 'declined';
  p_valid: boolean;
  p_good_vote: boolean;
  p_good_topic: boolean;
  p_sourceable: boolean;
  p_quality: string | null;
  p_note: string | null;
  p_matched_queue_id: string | null;
}

export interface UpdateSourceRatingPayload {
  draftId: string;
  source_url: string;
  source_domain: string;
  rating: number;
  rated_by: string | null;
}

/**
 * Which queue a comment thread belongs to.
 *
 * Added 2026-09-23 (founder: "parked just needs a tag — like Triage — and then to
 * be handled in the queue like all the rest — again going through commenting
 * sequence same as Sourced"). `proposals` was given editor_notes / commented_at /
 * commented_by with the SAME names as sourced_queue precisely so one
 * implementation serves both rather than a second copy that drifts.
 *
 * Omitted means 'sourced_queue', so every existing queued mutation — including
 * ones already sitting in IndexedDB from before this change — keeps its old
 * behaviour with no migration of the local queue.
 */
export type CommentTable = 'sourced_queue' | 'proposals';

export interface CommentAppendPayload {
  draftId: string;
  /** Client-generated UUID — uniquely identifies this comment so retries are safe. */
  commentId: string;
  commentText: string;
  commentedBy: string | null;
  /** Defaults to 'sourced_queue' when absent. */
  table?: CommentTable;
}

export interface AgentReplyPayload {
  draftId: string;
  /** Client-generated UUID — uniquely identifies this reply. */
  replyId: string;
  replyText: string;
  recommendationTag: string | null;
  /** Defaults to 'sourced_queue' when absent. */
  table?: CommentTable;
}

// ============================================================================
// Dispatchers
// ============================================================================

/**
 * approve_draft — atomic multi-table approve.
 *
 * Server-tier path (preferred, 2026-05-19): POST to /api/admin/approve, a
 * Cloudflare Pages Function that calls the approve_draft RPC server-side
 * with the service-role key. Bypasses the browser-direct Supabase path
 * that hangs intermittently (editor-JWT refresh, PostgREST cold-start,
 * Cloudflare Access CORS interference on PATCH/POST). 5s upstream timeout.
 *
 * Fallback path: if the server-tier endpoint is unreachable (404 — function
 * not deployed yet; 5xx — server error), fall back to the original direct
 * Supabase RPC. This keeps the queue working during incremental rollout
 * of the server tier across endpoints.
 *
 * The approve_draft RPC is idempotent by p_draft_id, so server-tier
 * retry, server-then-fallback retry, and queue-level retry are all safe.
 */
export async function dispatchApproveDraft(
  client: SupabaseClient,
  payload: ApproveDraftPayload,
): Promise<DispatchResult> {
  const serverResult = await callServerTier('/api/admin/approve', payload);
  if (serverResult.ok) return { error: null };
  if (!serverResult.fallback) return { error: serverResult.error };
  console.warn(`[dispatch] ${serverResult.error}; falling back to direct Supabase`);

  // Fallback: direct Supabase RPC (the pre-server-tier path).
  const { error } = await doors(client).rpc('approve_draft', payload as unknown as Record<string, unknown>);
  return { error: error?.message ?? null };
}

/**
 * update_status_held — flip the row to status='held' with a timestamp.
 * Idempotent: re-setting status to its current value is a no-op.
 *
 * Server-tier path (preferred): POST to /api/admin/hold. Fallback: direct
 * Supabase PATCH with the Rule #34 .select('id') RLS-denial guard.
 */
export async function dispatchUpdateStatusHeld(
  client: SupabaseClient,
  payload: UpdateStatusHeldPayload,
): Promise<DispatchResult> {
  const serverResult = await callServerTier('/api/admin/hold', payload);
  if (serverResult.ok) return { error: null };
  if (!serverResult.fallback) return { error: serverResult.error };
  console.warn(`[dispatch] ${serverResult.error}; falling back to direct Supabase`);

  const { data, error } = await doors(client).rpc('sourced_queue_update', {
    p_id: payload.draftId,
    p_fields: { status: 'held', held_at: new Date().toISOString() },
  });
  if (error) return { error: error.message };
  if (!data) {
    return { error: 'Save failed — row not found or permission denied (Rule #34 guard)' };
  }
  return { error: null };
}

/**
 * reject_draft — atomic two-write reject via the server-side RPC.
 *
 * The RPC encapsulates (a) INSERT into vote_rejections (audit log with
 * full row snapshot) and (b) UPDATE sourced_queue.status='rejected' +
 * metadata in a single Postgres transaction. Mirrors approve_draft's
 * shape; idempotent by p_draft_id (re-rejecting yields the same end
 * state but inserts a second audit row — acceptable; the rejection log
 * is intentionally append-only).
 *
 * Plan reference: docs/archive/admin-rebuild/admin-fragility-rebuild-plan.md § Item 1.
 * RPC source: migration add_reject_draft_rpc (2026-05-19).
 */
export async function dispatchRejectDraft(
  client: SupabaseClient,
  payload: RejectDraftPayload,
): Promise<DispatchResult> {
  const serverResult = await callServerTier('/api/admin/reject', payload);
  if (serverResult.ok) return { error: null };
  if (!serverResult.fallback) return { error: serverResult.error };
  console.warn(`[dispatch] ${serverResult.error}; falling back to direct Supabase`);

  const { error } = await doors(client).rpc('reject_draft', payload as unknown as Record<string, unknown>);
  return { error: error?.message ?? null };
}

/**
 * update_status_rejected — flip the row to status='rejected' with reason.
 * Idempotent.
 *
 * NOTE: this is the LEGACY single-table reject path; the editor flow uses
 * `reject_draft` (above) which also writes the vote_rejections audit row.
 * Kept for any future flow that needs status-only rejection without an
 * audit copy (none today).
 */
export async function dispatchUpdateStatusRejected(
  client: SupabaseClient,
  payload: UpdateStatusRejectedPayload,
): Promise<DispatchResult> {
  // The column is `reject_note`. This wrote `reject_notes` until 2026-09-28 — a column that does
  // not exist, so this legacy path could only ever fail (found during CS-5).
  const { data, error } = await doors(client).rpc('sourced_queue_update', {
    p_id: payload.draftId,
    p_fields: {
      status: 'rejected',
      rejected_at: new Date().toISOString(),
      reject_reason: payload.reject_reason,
      reject_note: payload.reject_notes,
      rejected_by: payload.rejected_by,
    },
  });
  if (error) return { error: error.message };
  if (!data) {
    return { error: 'Save failed — row not found or permission denied' };
  }
  return { error: null };
}

/**
 * update_content — generic content-field update (poll_question,
 * proposal_text, proposal_paragraphs, etc.). Idempotent: overwriting
 * with the same value is a no-op.
 *
 * NOTE: this dispatcher assumes the editor's intent is the LATEST
 * value typed. If the editor edits the same field twice in rapid
 * succession before the first enqueue syncs, the second enqueue
 * overwrites the first — which is correct. The queue still records both
 * for audit, but only the second value lands in sourced_queue.
 */
export async function dispatchUpdateContent(
  client: SupabaseClient,
  payload: UpdateContentPayload,
): Promise<DispatchResult> {
  const serverResult = await callServerTier('/api/admin/edit', payload);
  if (serverResult.ok) return { error: null };
  if (!serverResult.fallback) return { error: serverResult.error };
  console.warn(`[dispatch] ${serverResult.error}; falling back to direct Supabase`);

  const { data, error } = await doors(client).rpc('sourced_queue_update', {
    p_id: payload.draftId,
    p_fields: payload.fields,
  });
  if (error) return { error: error.message };
  if (!data) {
    return { error: 'Save failed — row not found or permission denied' };
  }
  return { error: null };
}

/**
 * update_source_rating — record an editor rating in source_quality_signals.
 *
 * Idempotency via pre-check (2026-05-19 fix): INSERT into source_quality_signals
 * is naturally not idempotent, and queue retries can fire multiple times for
 * the same logical rating event. Without de-duplication, every retry produces
 * a duplicate signal row, which inflates the domain's recomputed quality
 * score (a 5★ rating retried 8 times reads as 8 × 5★ in the aggregator).
 *
 * Approach: SELECT existing rows matching (vote_id, source_url, signal_type,
 * rated_by) before INSERT. If any exist, treat as already-applied → return
 * success without inserting again. TOCTOU race exists in theory (another
 * INSERT could land between SELECT and INSERT) but in practice the
 * single-editor admin pattern + the queue's per-record inflight guard
 * prevent the same record from firing in parallel. The long-term fix is a
 * `UNIQUE (vote_id, source_url, rated_by) WHERE signal_type='editor_rating'`
 * partial constraint at the DB level — that's a migration for a future pass.
 */
export async function dispatchUpdateSourceRating(
  client: SupabaseClient,
  payload: UpdateSourceRatingPayload,
): Promise<DispatchResult> {
  const serverResult = await callServerTier('/api/admin/rate-source', payload);
  if (serverResult.ok) return { error: null };
  if (!serverResult.fallback) return { error: serverResult.error };
  console.warn(`[dispatch] ${serverResult.error}; falling back to direct Supabase`);

  // Pre-check: does an editor_rating signal already exist for this
  // (row, url, editor) combo? If so, skip the INSERT.
  let dedupQuery = doors(client)
    .rpc('source_quality_signals')
    .select('id')
    .eq('vote_id', payload.draftId)
    .eq('source_url', payload.source_url)
    .eq('signal_type', 'editor_rating')
    .limit(1);
  // rated_by may be null (no user attribution); match on null explicitly.
  dedupQuery = payload.rated_by === null
    ? dedupQuery.is('rated_by', null)
    : dedupQuery.eq('rated_by', payload.rated_by);
  const { data: existing, error: checkErr } = await dedupQuery;
  if (checkErr) return { error: `dedup check failed: ${checkErr.message}` };
  if (existing && existing.length > 0) {
    // Already applied — idempotent success.
    return { error: null };
  }

  const { error } = await doors(client).rpc('source_quality_signals_insert', {
    p_fields: {
      vote_id: payload.draftId,
      source_url: payload.source_url,
      source_domain: payload.source_domain,
      signal_type: 'editor_rating',
      rating: payload.rating,
      rated_by: payload.rated_by,
      notes: null,
    },
  });
  return { error: error?.message ?? null };
}

/**
 * triage_proposal — record an editor's triage verdict on a member proposal.
 *
 * No server-tier call: there is no /api/admin/triage Pages Function, and
 * callServerTier would add a 404 round-trip to every verdict. This follows
 * dispatchUpdateStatusRejected's precedent of going direct when no endpoint
 * exists.
 *
 * Idempotency: votd_triage_proposal is an UPDATE of the verdict columns plus one
 * INSERT into source_quality_signals. Re-running it lands on the same end state,
 * but the signal INSERT is not naturally idempotent — the same caveat
 * update_source_rating carries. The RPC swallows a failed signal INSERT with a
 * warning rather than losing the editor's verdict, so a retry cannot strand the
 * verdict itself.
 */
export async function dispatchTriageProposal(
  client: SupabaseClient,
  payload: TriageProposalPayload,
): Promise<DispatchResult> {
  const { error } = await doors(client).rpc('votd_triage_proposal', payload as unknown as Record<string, unknown>);
  return { error: error?.message ?? null };
}

/**
 * comment_append — append an editor comment to sourced_queue.editor_notes
 * and flip status to 'commenting'.
 *
 * IDEMPOTENCY TRICK (per plan doc Risk #3 mitigation): the SQL UPDATE
 * only appends if the commentId isn't already present in editor_notes.
 * This makes the op safe to retry — the second attempt sees the first
 * one's marker and does nothing.
 *
 * Marker format embedded in the comment body: `<!-- comment-id:UUID -->`
 * — invisible in the rendered admin view but findable in the raw text.
 */
export async function dispatchCommentAppend(
  client: SupabaseClient,
  payload: CommentAppendPayload,
): Promise<DispatchResult> {
  const marker = `<!-- comment-id:${payload.commentId} -->`;
  const table  = payload.table ?? 'sourced_queue';

  // First fetch — check if the marker is already in editor_notes (i.e.,
  // a previous retry already landed). If yes, this is a no-op success.
  const { data: existing, error: fetchErr } = await doors(client)
    .rpc(table)
    .select('editor_notes')
    .eq('id', payload.draftId)
    .single();
  if (fetchErr) return { error: fetchErr.message };
  if (existing?.editor_notes && existing.editor_notes.includes(marker)) {
    return { error: null }; // already applied — idempotent success
  }

  // Append the comment with marker.
  const existingNotes = existing?.editor_notes ?? '';
  const separator = existingNotes ? '\n\n' : '';
  const newNotes = `${existingNotes}${separator}${payload.commentText}\n${marker}`;

  const { data, error } = await doors(client).rpc(`${table}_update`, {
    p_id: payload.draftId,
    p_fields: {
      editor_notes: newNotes,
      status: 'commenting',
      commented_at: new Date().toISOString(),
      commented_by: payload.commentedBy,
    },
  });
  if (error) return { error: error.message };
  if (!data) {
    return { error: 'Save failed — row not found or permission denied' };
  }
  return { error: null };
}

/**
 * agent_reply — append a Claude-side reply under an editor's comment.
 * Same idempotency pattern as comment_append, but the marker reflects
 * the reply context and the status stays at 'commenting' (per the
 * Rule #27 agent-reply contract).
 *
 * The reply format includes the "Claude (cowork)" attribution line that
 * EditableField.splitClaudeReply() detects to render the reply in
 * brand-orange.
 */
export async function dispatchAgentReply(
  client: SupabaseClient,
  payload: AgentReplyPayload,
): Promise<DispatchResult> {
  const marker = `<!-- reply-id:${payload.replyId} -->`;
  const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
  const table = payload.table ?? 'sourced_queue';

  const { data: existing, error: fetchErr } = await doors(client)
    .rpc(table)
    .select('editor_notes')
    .eq('id', payload.draftId)
    .single();
  if (fetchErr) return { error: fetchErr.message };
  if (existing?.editor_notes && existing.editor_notes.includes(marker)) {
    return { error: null }; // already applied
  }

  const existingNotes = existing?.editor_notes ?? '';
  const attribution = `──── Claude (cowork) ${today} ────`;
  const newNotes = `${existingNotes}\n\n${attribution}\n${payload.replyText}\n${marker}`;

  const { data, error } = await doors(client).rpc(`${table}_update`, {
    p_id: payload.draftId,
    p_fields: { editor_notes: newNotes },
  });
  if (error) return { error: error.message };
  if (!data) {
    return { error: 'Save failed — row not found or permission denied' };
  }
  return { error: null };
}

// ============================================================================
// Router — sync.ts calls this with the op + payload from a queue record
// ============================================================================

export async function dispatch(
  op: string,
  payload: Record<string, unknown>,
  client: SupabaseClient,
): Promise<DispatchResult> {
  switch (op) {
    case 'approve_draft':
      return dispatchApproveDraft(client, payload as unknown as ApproveDraftPayload);
    case 'reject_draft':
      return dispatchRejectDraft(client, payload as unknown as RejectDraftPayload);
    case 'update_status_held':
      return dispatchUpdateStatusHeld(client, payload as unknown as UpdateStatusHeldPayload);
    case 'update_status_rejected':
      return dispatchUpdateStatusRejected(client, payload as unknown as UpdateStatusRejectedPayload);
    case 'update_content':
      return dispatchUpdateContent(client, payload as unknown as UpdateContentPayload);
    case 'triage_proposal':
      return dispatchTriageProposal(client, payload as unknown as TriageProposalPayload);
    case 'update_source_rating':
      return dispatchUpdateSourceRating(client, payload as unknown as UpdateSourceRatingPayload);
    case 'comment_append':
      return dispatchCommentAppend(client, payload as unknown as CommentAppendPayload);
    case 'agent_reply':
      return dispatchAgentReply(client, payload as unknown as AgentReplyPayload);
    default:
      return { error: `dispatch: unknown op '${op}'` };
  }
}
